import { useState } from 'react';
import { t, type DocumentNotesKind } from '@bms2/shared';
import { useViewerZone } from '../../shell/ViewerZone';
import { PAGES_PATH, useApi } from './api';
import { DocumentNotesPanel } from './DocumentNotesPanel';
import styles from './DocumentPage.module.css';
import type { TileRow } from './tileItems';

/** A document's notes read (decision 0951): relative to the API root, never repeating `/api`. */
export function documentNotesPath(recordType: DocumentNotesKind, recordId: string): string {
  return `${PAGES_PATH}/notes/${recordType}/${encodeURIComponent(recordId)}`;
}

/** What the notes read answers: the rows the Internal Notes tile draws, the viewer, and whether the composer is offered. */
export interface DocumentNotesAnswer {
  rows: TileRow[];
  personId?: string;
  mayAdd?: boolean;
}

/**
 * 🔒 The document notes panel on a document page whose answer carries no notes
 * of its own (decision 0951, amending 0470; T336). Product owner, 2026-09-26:
 * *"there is no place to comment on the documents… i mean notes for documents"*.
 * It reads the document's notes, draws the one panel the Quote and the Manual
 * Journal draw — the composer at its top — and reads again after every write.
 *
 * 🔨 A read the server refuses draws nothing, not an empty panel: an outside
 * login is refused the notes read at the boundary (piece 21), and a panel
 * saying *No notes yet* to them would be a panel about notes they may not read.
 *
 * 🔒 The notes travel in the document's own page read where its kind carries
 * them (T715; decision 1456), so the PDF view asks nothing more to draw them: a
 * page hands them in as `carried`, null where the server withheld them. Only a
 * write reads the notes route again, and the carried rows stand until it answers.
 * 💰 The panel mounted after the page read and asked for its notes second, in
 * series, and the rail beside the sheet hid while they were in flight, so the
 * sheet drew full width and shrank when they came.
 *
 * 🔒 A read still in flight draws the panel's heading alone, so the rail beside
 * the sheet stands at its width from the first paint (decision 1456).
 */
export function DocumentNotes({ recordType, recordId, recordLabel, apiBaseUrl = '', onError, carried }: {
  recordType: DocumentNotesKind;
  recordId: string;
  /** The document's own name, for the note window's fixed chip. */
  recordLabel?: string | undefined;
  apiBaseUrl?: string;
  onError?: (message: string) => void;
  /** The notes the page read carried: an answer, null where withheld, or undefined where the kind carries none and the panel reads them itself. */
  carried?: DocumentNotesAnswer | null | undefined;
}) {
  const zone = useViewerZone();
  const hasCarried = carried !== undefined;
  /** 🔨 Set by the first write, after which the panel reads its own notes; until then the page's rows are the answer. */
  const [reading, setReading] = useState(!hasCarried);
  const notes = useApi<DocumentNotesAnswer>(reading && recordId ? documentNotesPath(recordType, recordId) : null, apiBaseUrl);
  const onChanged = () => (reading ? notes.reload() : setReading(true));
  const fetched = notes.state.kind === 'ready' ? notes.state.data : null;
  /*
   * 🔒 A later page read supersedes the panel's own read (found in review, 2026-09-30).
   * 💰 After the first write the panel's answer shadowed the page's rows for good, so a
   * note another person added never showed on the next reload. When the carried rows
   * change, the panel goes back to them and stops reading; the answer it held is marked
   * superseded, so a later write shows the page's rows until its own read answers.
   */
  const [readAgainst, setReadAgainst] = useState(carried);
  const [superseded, setSuperseded] = useState<DocumentNotesAnswer | null>(null);
  if (hasCarried && carried !== readAgainst) {
    setReadAgainst(carried);
    if (reading) {
      setReading(false);
      setSuperseded(fetched);
    }
  }
  if (hasCarried && carried === null) return null;
  const own = fetched !== null && fetched !== superseded ? fetched : null;
  const answer = own ?? (hasCarried ? carried : null);
  if (answer === null) {
    if (notes.state.kind !== 'loading') return null;
    return (
      <section className={styles.block} aria-busy="true" data-document-notes-reading={recordId}>
        <h2 className={styles.sectionLabel}>{t('documentNotes.title')}</h2>
      </section>
    );
  }
  return (
    <DocumentNotesPanel
      recordType={recordType}
      recordId={recordId}
      {...(recordLabel ? { recordLabel } : {})}
      rows={answer.rows ?? []}
      personId={answer.personId ?? ''}
      timeZone={zone}
      apiBaseUrl={apiBaseUrl}
      onChanged={onChanged}
      {...(onError ? { onError } : {})}
      mayAdd={answer.mayAdd !== false}
    />
  );
}

/**
 * The notes a document's page read carried (T715; decision 1456): the answer,
 * null where the server withheld them, or undefined where the read carries no
 * such key and the panel reads them itself.
 */
export function carriedNotes(answer: object): DocumentNotesAnswer | null | undefined {
  return 'notes' in answer ? ((answer as { notes?: DocumentNotesAnswer | null }).notes ?? null) : undefined;
}
